import { createFileRoute } from "@tanstack/react-router";
import { ExternalLink, ScrollText } from "lucide-react";

import { useLang } from "@/contexts/lang";

export const Route = createFileRoute("/terms-of-service")({
	headers: () => ({
		"Cache-Control":
			"public, max-age=0, s-maxage=86400, stale-while-revalidate=604800",
	}),
	head: () => ({
		meta: [
			{ title: "Услови за користење | Македонијатурист АД" },
			{
				name: "description",
				content:
					"Услови за користење на веб-страницата на Македонијатурист АД. Правила и регулативи за пристап и користење на нашите услуги.",
			},
			{
				name: "robots",
				content: "noindex, follow",
			},
		],
		links: [
			{
				rel: "canonical",
				href: "https://makedonijaturist.com.mk/terms-of-service",
			},
		],
	}),
	component: TermsOfServicePage,
});

function renderText(text: string) {
	// Pure URL
	if (/^https?:\/\//.test(text)) {
		return (
			<a
				href={text}
				target="_blank"
				rel="noopener noreferrer"
				className="inline-flex items-center gap-1 text-accent hover:underline underline-offset-2 break-all"
			>
				{text}
				<ExternalLink className="h-3 w-3 shrink-0" />
			</a>
		);
	}
	// Pure email
	if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(text)) {
		return (
			<a
				href={`mailto:${text}`}
				className="text-accent hover:underline underline-offset-2"
			>
				{text}
			</a>
		);
	}
	// Inline email or www URL within text
	const parts = text.split(/([\w.+-]+@[\w.-]+\.[a-zA-Z]{2,}|www\.[^\s]+)/g);
	if (parts.length > 1) {
		return (
			<>
				{parts.map((part) => {
					if (/^[\w.+-]+@[\w.-]+\.[a-zA-Z]{2,}$/.test(part)) {
						return (
							<a
								key={part}
								href={`mailto:${part}`}
								className="text-accent hover:underline underline-offset-2"
							>
								{part}
							</a>
						);
					}
					if (/^www\./.test(part)) {
						return (
							<a
								key={part}
								href={`https://${part}`}
								target="_blank"
								rel="noopener noreferrer"
								className="text-accent hover:underline underline-offset-2"
							>
								{part}
							</a>
						);
					}
					return part;
				})}
			</>
		);
	}
	return text;
}

export function TermsOfServicePage() {
	const t = useLang();
	const { heading, lastUpdated, sections } = t.termsOfService;

	return (
		<main className="bg-background">
			{/* Hero */}
			<section className="mt-grain relative overflow-hidden bg-primary text-white">
				<div className="pointer-events-none absolute inset-0">
					<div className="absolute -top-[20%] right-0 h-[600px] w-[600px] rounded-full bg-[radial-gradient(closest-side,rgba(201,161,90,0.12),transparent)] blur-[100px]" />
					<div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:60px_60px] [mask-image:radial-gradient(ellipse_at_center,black_20%,transparent_70%)]" />
				</div>
				<div className="relative mx-auto max-w-7xl px-4 sm:px-6 py-12 sm:py-16 md:py-24">
					<div className="max-w-2xl">
						<div className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-accent/20 text-accent mb-6">
							<ScrollText className="h-5 w-5" />
						</div>
						<h1 className="font-display text-3xl sm:text-4xl md:text-5xl font-medium tracking-tight leading-[1.08]">
							{heading}
						</h1>
						<p className="mt-4 text-sm text-white/40 font-light tracking-wide">
							{lastUpdated}
						</p>
					</div>
				</div>
			</section>

			{/* Content */}
			<section className="bg-background py-12 sm:py-16 md:py-24">
				<div className="mx-auto max-w-3xl px-4 sm:px-6">
					<div className="space-y-5 sm:space-y-6">
						{sections.map((section, i) => (
							<div key={section.heading} className="py-2">
								<div className="flex items-baseline gap-3.5 mb-3">
									<span className="shrink-0 text-[10px] font-bold tracking-[0.2em] text-accent/50">
										{String(i + 1).padStart(2, "0")}
									</span>
									<h2 className="font-display text-base sm:text-lg font-semibold text-primary leading-snug">
										{section.heading}
									</h2>
								</div>

								<div className="pl-[26px] border-l border-border space-y-2">
									{section.paragraphs.map((p) => (
										<p
											key={p}
											className="text-sm text-foreground/65 font-light leading-relaxed"
										>
											{renderText(p)}
										</p>
									))}
									{section.items && section.items.length > 0 && (
										<ul className="pt-1 space-y-1.5">
											{section.items.map((item) => (
												<li
													key={item}
													className="flex items-start gap-2.5 text-sm text-foreground/65 font-light leading-relaxed"
												>
													<span className="mt-[7px] shrink-0 h-1 w-1 rounded-full bg-accent/70" />
													{renderText(item)}
												</li>
											))}
										</ul>
									)}
								</div>
							</div>
						))}
					</div>
				</div>
			</section>
		</main>
	);
}
